import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2, Send } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { buildWhatsAppUrl } from "@/data/translations";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function LeadForm({ country }) {
  const { t, lang, isRTL } = useLanguage();
  const L = t.countryPage.lead;
  const countryName = lang === "ar" ? country.name_ar : country.name_en;

  const [form, setForm] = useState({ name: "", phone: "", link: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((err) => ({ ...err, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = L.validationRequired;
    if (!form.phone.trim()) e.phone = L.validationRequired;
    if (!form.link.trim()) e.link = L.validationRequired;
    else if (!/^https?:\/\/.+/i.test(form.link.trim())) e.link = L.validationLink;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      await axios.post(`${API_BASE}/api/leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        product_link: form.link.trim(),
        country_code: country.code,
        country_name: countryName,
      }, { timeout: 10000 });
      setStatus("success");
      // Auto-open WhatsApp with pre-filled message
      const msg = `${t.common.whatsappCountryMsg(countryName)}\n\n${L.name}: ${form.name}\n${L.phone}: ${form.phone}\n${L.link}: ${form.link}`;
      const url = buildWhatsAppUrl(msg);
      setTimeout(() => window.open(url, "_blank", "noopener,noreferrer"), 900);
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <section
      id="quote"
      className="relative overflow-hidden rounded-[24px] border border-[#F37021]/15 bg-gradient-to-br from-[#FDF1E8] via-white to-[#FDF1E8] p-6 sm:p-10 md:rounded-[28px] md:p-14 dark:border-white/10 dark:bg-[linear-gradient(135deg,#101010_0%,#181818_100%)]"
      data-testid="lead-form-section"
    >
      <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#F37021]/20 blur-[100px] pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-[#F37021]/15 blur-[120px] pointer-events-none" />

      <div className="relative grid lg:grid-cols-2 gap-8 md:gap-10 items-center">
        <div>
          <span className="inline-block text-xs font-semibold tracking-[0.3em] uppercase text-[#F37021] mb-3 md:mb-4">
            — {L.eyebrow}
          </span>
          <h3 className="mb-3 font-display text-3xl font-bold leading-[1.08] text-[#111] sm:text-4xl md:mb-4 md:text-5xl dark:text-white">
            {L.title}
          </h3>
          <p className="max-w-md text-sm leading-relaxed text-[#555] sm:text-base md:text-lg dark:text-[#BAC4D4]">
            {L.subtitle}
          </p>

          <div className="mt-5 inline-flex max-w-full items-start gap-2 rounded-2xl border border-black/5 bg-white px-3.5 py-2 text-xs text-[#555] sm:text-sm md:mt-6 dark:border-white/10 dark:bg-white/10 dark:text-[#BAC4D4]">
            <span className="w-2 h-2 rounded-full bg-[#25D366] animate-pulse mt-1.5 shrink-0" />
            <span>{L.privacy}</span>
          </div>
        </div>

        <motion.form
          onSubmit={handleSubmit}
          noValidate
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="space-y-4 rounded-2xl border border-black/5 bg-white p-6 shadow-[0_20px_60px_rgba(0,0,0,0.06)] md:p-7 dark:border-white/10 dark:bg-[#121212]"
          data-testid="lead-form"
        >
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#555] dark:text-[#BAC4D4]">
              {L.name}
            </label>
            <input
              type="text"
              value={form.name}
              onChange={update("name")}
              placeholder={L.namePlaceholder}
              className={`h-12 w-full rounded-xl border bg-[#FAFAFA] px-4 text-sm transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#F37021]/20 dark:bg-[#121212] dark:text-white ${
                errors.name ? "border-red-400 focus:border-red-400" : "border-black/10 focus:border-[#F37021]"
              }`}
              data-testid="lead-input-name"
            />
            {errors.name && <p className="text-xs text-red-500 mt-1.5" data-testid="lead-error-name">{errors.name}</p>}
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#555] dark:text-[#BAC4D4]">
              {L.phone}
            </label>
            <input
              type="tel"
              value={form.phone}
              onChange={update("phone")}
              placeholder={L.phonePlaceholder}
              dir="ltr"
              className={`h-12 w-full rounded-xl border bg-[#FAFAFA] px-4 text-sm transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#F37021]/20 dark:bg-[#121212] dark:text-white ${
                errors.phone ? "border-red-400 focus:border-red-400" : "border-black/10 focus:border-[#F37021]"
              } ${isRTL ? 'text-right' : 'text-left'}`}
              data-testid="lead-input-phone"
            />
            {errors.phone && <p className="text-xs text-red-500 mt-1.5" data-testid="lead-error-phone">{errors.phone}</p>}
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#555] dark:text-[#BAC4D4]">
              {L.link}
            </label>
            <input
              type="url"
              value={form.link}
              onChange={update("link")}
              placeholder={L.linkPlaceholder}
              dir="ltr"
              className={`h-12 w-full rounded-xl border bg-[#FAFAFA] px-4 text-sm transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#F37021]/20 dark:bg-[#121212] dark:text-white ${
                errors.link ? "border-red-400 focus:border-red-400" : "border-black/10 focus:border-[#F37021]"
              } ${isRTL ? 'text-right' : 'text-left'}`}
              data-testid="lead-input-link"
            />
            {errors.link && <p className="text-xs text-red-500 mt-1.5" data-testid="lead-error-link">{errors.link}</p>}
          </div>

          {status === "success" ? (
            <div
              className="flex items-start gap-3 p-4 rounded-xl bg-[#E7F8EE] border border-[#25D366]/30 text-[#0F5F2D]"
              data-testid="lead-success"
            >
              <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-[#25D366]" />
              <div>
                <div className="font-semibold text-sm">{L.successTitle}</div>
                <p className="text-sm mt-0.5 text-[#1A6B3D]">{L.successMsg}</p>
              </div>
            </div>
          ) : status === "error" ? (
            <div
              className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm"
              data-testid="lead-error"
            >
              {L.errorMsg}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={status === "loading" || status === "success"}
            className="group w-full h-14 px-6 rounded-full bg-[#F37021] hover:bg-[#E06015] text-white font-medium flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 disabled:opacity-70 disabled:hover:translate-y-0 shadow-[0_14px_30px_rgba(243,112,33,0.28)]"
            data-testid="lead-submit"
          >
            {status === "loading" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {L.submitting}
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                {L.submit}
                <ArrowRight className={`w-4 h-4 ms-1 transition-transform group-hover:translate-x-0.5 ${isRTL ? 'rotate-180 group-hover:-translate-x-0.5 group-hover:translate-x-0' : ''}`} />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
